import type { CSSProperties, ReactNode } from 'react'
import { useNarrow } from '@/components/console/use-narrow'
import { cn } from '@/lib/utils'

interface DataTableProps {
  /** Names the table for screen readers, e.g. "Call log". */
  label: string
  /** `<thead>` and `<tbody>`. Use the prototype's cell classes: `td.m`, `.num`, `.cellsum`, `tr.rowlink`. */
  children: ReactNode
  /**
   * The mobile fallback: one `.card` per row (`style={{ padding: 14 }}`). Shown
   * instead of the table at ≤860px. Omit it and the table scrolls in its wrapper.
   */
  cards?: ReactNode
  /** Drop the wrapper's border, e.g. inside a `card` with `padding: 0`. */
  bare?: boolean
  className?: string
  style?: CSSProperties
}

/**
 * A dense table in its scrolling wrapper (`.tablewrap`), with the prototype's
 * mobile card fallback: at ≤860px the cards replace it (`.agrid`, one column,
 * 10px apart). Nothing depends on hover.
 */
export function DataTable({ label, children, cards, bare, className, style }: DataTableProps) {
  const narrow = useNarrow()
  if (narrow && cards) {
    return (
      <div className="agrid" style={{ gridTemplateColumns: '1fr', gap: 10 }}>
        {cards}
      </div>
    )
  }
  return (
    <div className={cn('tablewrap', className)} style={{ ...(bare ? { border: 0 } : null), ...style }}>
      <table aria-label={label}>{children}</table>
    </div>
  )
}
